/**
 * HeatGrid — one cell per entity, coloured by state.
 *
 * Built for the portfolio view: an operator should see in about three seconds
 * whether anything is wrong and where. Cells are square-ish, dense, and scale to
 * a portfolio of a few dozen sites without scrolling.
 *
 * Encoding rules this follows:
 *   • The colours are the reserved status palette (good / warning / serious /
 *     critical), never the categorical series hues.
 *   • State is never carried by colour alone. Every cell shows its own short
 *     label, marks a non-healthy state with a shape in the corner, and names the
 *     state in text for assistive technology. A legend is always rendered.
 *   • Text keeps its ink tokens; the cell's tint carries identity of state.
 */
import React from 'react';
import { cn } from '../lib/utils';

export type HeatState = 'good' | 'warning' | 'serious' | 'critical' | 'unknown';

export interface HeatGridCell {
  id: string;
  /** Short label drawn in the cell — a property code reads best. */
  label: string;
  /** Full name, used for the tooltip and the accessible name. */
  title: string;
  state: HeatState;
  /** One line of detail for the tooltip and the accessible name. */
  detail?: string;
  onSelect?: () => void;
}

export interface HeatGridProps {
  cells: HeatGridCell[];
  /** Human-readable name per state, for the legend and screen readers. */
  stateLabels: Record<HeatState, string>;
  legendTitle?: string;
  className?: string;
}

const CELL_STYLE: Record<HeatState, string> = {
  good: 'bg-success-soft text-success border-success/30',
  warning: 'bg-warning-soft text-warning border-warning/30',
  serious: 'bg-accent-orange-soft text-accent-orange border-accent-orange/30',
  critical: 'bg-error-soft text-error border-error/40',
  unknown: 'bg-surface-3 text-text-muted border-border',
};

const SWATCH_STYLE: Record<HeatState, string> = {
  good: 'bg-success',
  warning: 'bg-warning',
  serious: 'bg-accent-orange',
  critical: 'bg-error',
  unknown: 'bg-text-faint',
};

/**
 * Redundant, non-colour marker. Healthy cells stay clean so attention lands on
 * the ones that need it; everything else is flagged by shape as well as hue.
 */
function StateMark({ state }: { state: HeatState }) {
  if (state === 'good') return null;
  if (state === 'unknown') {
    return (
      <svg viewBox="0 0 8 8" className="h-2 w-2" aria-hidden="true" fill="currentColor">
        <circle cx="4" cy="4" r="1.5" />
      </svg>
    );
  }
  if (state === 'critical') {
    return (
      <svg viewBox="0 0 8 8" className="h-2 w-2" aria-hidden="true" fill="currentColor">
        <rect x="1" y="1" width="6" height="6" rx="1" />
      </svg>
    );
  }
  // warning + serious: a triangle, the conventional caution shape.
  return (
    <svg viewBox="0 0 8 8" className="h-2 w-2" aria-hidden="true" fill="currentColor">
      <path d="M4 0.5 7.5 7H0.5Z" />
    </svg>
  );
}

export function HeatGrid({ cells, stateLabels, legendTitle, className }: HeatGridProps) {
  if (cells.length === 0) return null;

  // Only the states actually present are worth a legend entry.
  const present: HeatState[] = (['critical', 'serious', 'warning', 'good', 'unknown'] as HeatState[])
    .filter((state) => cells.some((cell) => cell.state === state));

  return (
    <div className={className}>
      <ul className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10">
        {cells.map((cell) => {
          const accessibleName = [cell.title, stateLabels[cell.state], cell.detail]
            .filter(Boolean)
            .join(' — ');
          const content = (
            <>
              <span className="flex w-full items-start justify-between gap-1">
                <span className="truncate text-xs font-semibold">{cell.label}</span>
                <StateMark state={cell.state} />
              </span>
              {cell.detail && (
                <span className="mt-auto block truncate text-[11px] opacity-80">{cell.detail}</span>
              )}
              <span className="sr-only">{accessibleName}</span>
            </>
          );
          const shared = cn(
            'flex min-h-[3.25rem] flex-col rounded-md border px-2 py-1.5 text-left',
            CELL_STYLE[cell.state],
          );
          return (
            <li key={cell.id}>
              {cell.onSelect ? (
                <button
                  type="button"
                  onClick={cell.onSelect}
                  title={accessibleName}
                  aria-label={accessibleName}
                  className={cn(
                    shared,
                    'w-full transition hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  )}
                >
                  {content}
                </button>
              ) : (
                <div title={accessibleName} className={shared}>
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {legendTitle && <span className="text-[11px] text-text-faint">{legendTitle}</span>}
        {present.map((state) => (
          <span key={state} className="inline-flex items-center gap-1.5 text-[11px] text-text-muted">
            <span aria-hidden="true" className={cn('h-2 w-2 rounded-sm', SWATCH_STYLE[state])} />
            {stateLabels[state]}
          </span>
        ))}
      </div>
    </div>
  );
}
